import { Box, Paper, Typography, Divider } from "@mui/material";
import './style.css';

interface RubricaSaldo {
    valor: number;
    despesas: number;
    saldo: number;
}

interface Rubricas {
    pessoal: RubricaSaldo;
    materialConsumo: RubricaSaldo;
    materialPermanente: RubricaSaldo;
}

interface ResumoData {
    valorProjeto: number;
    totalReceitas: number;
    totalDespesas: number;
    saldo: number;
    rubricas?: Rubricas;
}

interface FinancialSummaryProps {
    data: {
        resumo: ResumoData;
    };
    reportType: string;
}

const FinancialSummary = ({ data, reportType }: FinancialSummaryProps) => {    
    const formatCurrency = (value: number) => {
        return `R$ ${(value/100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    };

    // Estilo comum para todos os cards
    const cardStyle = {
        p: 2,
        backgroundColor: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '80px'
    };

    // Verificar se o projeto usa valores por rubrica
    const usaRubricas = data.resumo.rubricas !== undefined;

    // Determinar quantos cards principais serão exibidos
    const getMainCardsCount = () => {
        let count = 2; // Valor do Projeto + Saldo (sempre presentes)
        if (reportType === 'Receita' || reportType === 'Ambos') count++;
        if (reportType === 'Despesa' || reportType === 'Ambos') count++;
        return count;
    };

    const mainCardsCount = getMainCardsCount();

    return (
        <Box sx={{ mt: 4 }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 3, 
                    borderRadius: '8px', 
                    backgroundColor: '#f9f9f9'
                }}
            >
                <Typography variant="h6" sx={{ mb: 3, color: '#4aaa9c', fontWeight: 500 }}>
                    Resumo Financeiro
                </Typography>
                
                {/* Cards principais */}
                <Box 
                    sx={{ 
                        display: 'grid',
                        gridTemplateColumns: `repeat(${mainCardsCount}, 1fr)`,
                        gap: 2,
                        mb: usaRubricas ? 4 : 0
                    }}
                >
                    {/* Valor do Projeto */}
                    <Paper elevation={1} sx={cardStyle}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Valor do Projeto
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                            {formatCurrency(data.resumo.valorProjeto || 0)}
                        </Typography>
                    </Paper>
                    
                    {/* Total de Receitas */}
                    {(reportType === 'Receita' || reportType === 'Ambos') && (
                        <Paper elevation={1} sx={cardStyle}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Total de Receitas
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: '#2e7d32', mt: 1 }}>
                                {formatCurrency(data.resumo.totalReceitas || 0)}
                            </Typography>
                        </Paper>
                    )}
                    
                    {/* Total de Despesas */}
                    {(reportType === 'Despesa' || reportType === 'Ambos') && (
                        <Paper elevation={1} sx={cardStyle}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Total de Despesas
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: '#d32f2f', mt: 1 }}>
                                {formatCurrency(data.resumo.totalDespesas || 0)}
                            </Typography>
                        </Paper>
                    )}
                    
                    {/* Saldo */}
                    <Paper elevation={1} sx={{ 
                        ...cardStyle,
                        borderLeft: data.resumo.saldo >= 0 ? '4px solid #2e7d32' : '4px solid #d32f2f'
                    }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Saldo
                        </Typography>
                        <Typography 
                            variant="h6" 
                            fontWeight="bold"
                            sx={{ 
                                color: data.resumo.saldo >= 0 ? '#2e7d32' : '#d32f2f',
                                mt: 1
                            }}
                        >
                            {formatCurrency(data.resumo.saldo || 0)}
                        </Typography>
                    </Paper>
                </Box>

                {/* Seção para valores divididos por rubrica */}
                {usaRubricas && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" sx={{ mb: 3, color: '#4aaa9c', fontWeight: 500 }}>
                            Detalhamento por Categoria
                        </Typography>
                        
                        {/* Pessoal */}
                        <Typography variant="subtitle1" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
                            💼 Pessoal
                        </Typography>
                        <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 2,
                            mb: 3
                        }}>
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Valor
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.pessoal.valor || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Despesas
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#d32f2f', mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.pessoal.despesas || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={{ 
                                ...cardStyle,
                                borderLeft: (data.resumo.rubricas?.pessoal.saldo || 0) >= 0 ? 
                                    '4px solid #2e7d32' : '4px solid #d32f2f'
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Saldo
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    sx={{ 
                                        color: (data.resumo.rubricas?.pessoal.saldo || 0) >= 0 ? 
                                            '#2e7d32' : '#d32f2f',
                                        mt: 1
                                    }}
                                >
                                    {formatCurrency(data.resumo.rubricas?.pessoal.saldo || 0)}
                                </Typography>
                            </Paper>
                        </Box>
                        
                        {/* Material de Consumo */}
                        <Typography variant="subtitle1" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
                            📦 Material de Consumo
                        </Typography>
                        <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 2,
                            mb: 3
                        }}>
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Valor
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.materialConsumo.valor || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Despesas
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#d32f2f', mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.materialConsumo.despesas || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={{ 
                                ...cardStyle,
                                borderLeft: (data.resumo.rubricas?.materialConsumo.saldo || 0) >= 0 ? 
                                    '4px solid #2e7d32' : '4px solid #d32f2f'
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Saldo
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    sx={{ 
                                        color: (data.resumo.rubricas?.materialConsumo.saldo || 0) >= 0 ? 
                                            '#2e7d32' : '#d32f2f',
                                        mt: 1
                                    }}
                                >
                                    {formatCurrency(data.resumo.rubricas?.materialConsumo.saldo || 0)}
                                </Typography>
                            </Paper>
                        </Box>
                        
                        {/* Material Permanente */}
                        <Typography variant="subtitle1" sx={{ mb: 2, color: '#666', fontWeight: 500 }}>
                            🏗️ Material Permanente
                        </Typography>
                        <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 2
                        }}>
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Valor
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.materialPermanente.valor || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={cardStyle}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Despesas
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#d32f2f', mt: 1 }}>
                                    {formatCurrency(data.resumo.rubricas?.materialPermanente.despesas || 0)}
                                </Typography>
                            </Paper>
                            
                            <Paper elevation={1} sx={{ 
                                ...cardStyle,
                                borderLeft: (data.resumo.rubricas?.materialPermanente.saldo || 0) >= 0 ? 
                                    '4px solid #2e7d32' : '4px solid #d32f2f'
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Saldo
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    fontWeight="bold"
                                    sx={{ 
                                        color: (data.resumo.rubricas?.materialPermanente.saldo || 0) >= 0 ? 
                                            '#2e7d32' : '#d32f2f',
                                        mt: 1
                                    }}
                                >
                                    {formatCurrency(data.resumo.rubricas?.materialPermanente.saldo || 0)}
                                </Typography>
                            </Paper>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default FinancialSummary;